import React, { FC } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import { Element, scale, notchTop } from '@components/frame';
import { isPhone } from '@helpers';
import ColorsWrapper from '@styles/colors';
import TouchableElement from '@components/common/touchableElement';
import { font } from '@styles/fonts';
import RotateIcon from '@assets/icons/rotate.svg';
import DiscardChangesPopup from '@v2/components/imageHandling/components/Cropper/components/DiscardChangesPopup';
import { CropperWebProps } from '@v2/components/imageHandling/components/Cropper/components/CropperWeb';
import CropperTabletMobile, { CropperTabletMobileProps } from '@v2/components/imageHandling/components/Cropper/components/CropperTabletMobile';
import { useManageCropperMobileTablet } from '@v2/components/imageHandling/components/Cropper/hooks/useManageCropperMobileTablet';
import ChooseCropModeModal from '@v2/components/imageHandling/components/Cropper/components/ChooseCropModeModal';
import { useManageChooseCropModeModal } from '@v2/components/imageHandling/components/Cropper/hooks/useManageChooseCropModeModal';

export interface CropperTabletProps extends Pick<CropperWebProps, 'C' | 'image' | 'onSave'> {
  triggerShow: () => void;
  isNewImage?: boolean;
}

const CropperTablet: FC<CropperTabletProps> = (props) => {
  const { triggerShow, C, onSave, image, isNewImage = false } = props;

  const {
    angle,
    modeRef,
    onCancelButtonPress,
    onRotateImage,
    onSaveButtonPress,
    showDiscardChangesPopup,
    triggerDiscardChangesPopup,
    cropRef,
    showChooseCropModeModalOpen,
    setShowChooseCropModeModalOpen,
    pendingCropData,
    setPendingCropData,
    processCropData
  } = useManageCropperMobileTablet({ image, onSave, isNewImage });

  const cropperProps: CropperTabletMobileProps = {
    image,
    cropRef,
    angle,
    onSave,
    triggerShow,
    C,
    modeRef,
    triggerDiscardChangesPopup,
    setShowChooseCropModeModalOpen,
    setPendingCropData,
    isNewImage,
  };

  const { config, onCloseShowChooseCropModeModalOpen } = useManageChooseCropModeModal({
    closeCrop: triggerShow,
    image,
    pendingCropData,
    processCropData,
    setPendingCropData,
    setShowChooseCropModeModalOpen,
  });

  return [
    <DiscardChangesPopup {...{ showDiscardChangesPopup, triggerDiscardChangesPopup, cancelCrop: triggerShow, C }} key="DiscardChangesPopup" />,
    <ChooseCropModeModal
      key="ChooseCropModeModal"
      isVisible={showChooseCropModeModalOpen}
      onClose={onCloseShowChooseCropModeModalOpen}
      C={C}
      config={config}
      containerStyle={{
        top: scale(isPhone ? 51.5 + notchTop : 74),
        right: scale(21),
        alignItems: 'flex-end',
      }}
    />,
    <View
      style={{
        position: 'absolute',
        zIndex: 11000,
        width: '100%',
        height: '100%',
        backgroundColor: C('Label/Light/AlwaysDark Primary'),
      }}
      key="CropperBox"
    >
      <Element
        {...{
          config: {
            position: {
              top: notchTop,
              right: 0,
              left: 0,
            },
            height: 39.5,
            backgroundColor: C('Label/Light/AlwaysDark Primary'),
          },
        }}
      >
        <Element
          {...{
            config: {
              position: {
                top: 14,
                left: 21,
              },
              zIndex: 100,
            },
          }}
        >
          <TouchableElement
            onPress={() => {
              onCancelButtonPress();
            }}
          >
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle]}>
                <Text style={[{ color: C(pressHoverColor('Label / Light / AlwaysLight Primary')) }, font['Subhead 1']]}>{i18n.t('Cancel')}</Text>
              </View>
            )}
          </TouchableElement>
        </Element>
        <Element
          {...{
            config: {
              position: {
                top: 9,
                left: isPhone ? 90 : 118,
              },
              zIndex: 100,
            },
          }}
        >
          <TouchableElement onPress={onRotateImage}>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle]}>
                <RotateIcon width={scale(22)} height={scale(24)} />
              </View>
            )}
          </TouchableElement>
        </Element>
        <Element
          {...{
            config: {
              position: {
                top: 17,
                left: 0,
                right: 0,
              },
            },
          }}
        >
          <TouchableElement>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { width: '100%' }]}>
                <Text
                  style={[
                    {
                      color: C(pressHoverColor('Label / Light / AlwaysLight Primary')),
                      textTransform: 'uppercase',
                      width: '100%',
                      textAlign: 'center',
                    },
                    font.Footnote,
                  ]}
                >
                  {i18n.t('Edit')}
                </Text>
              </View>
            )}
          </TouchableElement>
        </Element>
        <Element
          {...{
            config: {
              position: {
                top: 14,
                right: 21,
              },
              zIndex: 100,
            },
          }}
        >
          <TouchableElement onPress={onSaveButtonPress}>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle]}>
                <Text style={[{ color: C(pressHoverColor('Label / Dark / Favorite')) }, font['Subhead 1']]}>{i18n.t('Done')}</Text>
              </View>
            )}
          </TouchableElement>
        </Element>
      </Element>
      <CropperTabletMobile {...cropperProps} />
    </View>,
  ];
};

export default ColorsWrapper(CropperTablet);
